import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail, setStoredToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function SetInitialPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const token = params.get("token");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/set-initial-password", { token, new_password: newPassword });
      setStoredToken(data.access_token);
      setUser(data);
      toast.success("Contraseña creada. Ahora activa tu plan para empezar a usar CERH.");
      navigate("/admin/facturacion");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="w-full max-w-md text-center">
          <div className="bg-card border border-petroleum/10 rounded-2xl p-10 shadow-[0_8px_30px_rgba(14,79,79,0.08)]">
            <p className="text-charcoal/80">Falta el enlace para crear tu contraseña. Confirma tu correo de nuevo desde el correo que te mandamos.</p>
            <Link to="/login" className="inline-block mt-6 text-petroleum font-semibold underline">Ir a iniciar sesión</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <ShieldCheck className="w-7 h-7 text-petroleum" strokeWidth={1.5} />
          <span className="font-heading text-xl font-bold text-charcoal">CERH</span>
        </Link>
        <div className="bg-card border border-petroleum/10 rounded-2xl p-8 shadow-[0_8px_30px_rgba(14,79,79,0.08)]">
          <h1 className="font-heading text-2xl font-bold text-charcoal mb-1">Crea tu contraseña</h1>
          <p className="text-sm text-charcoal/60 mb-6">Correo confirmado. Con esto ya tienes tu cuenta, solo falta activar tu plan.</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Contraseña</Label>
              <Input data-testid="set-initial-password-input" type="password" required minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Mínimo 8 caracteres" />
            </div>
            <div>
              <Label>Confirma tu contraseña</Label>
              <Input data-testid="set-initial-password-confirm-input" type="password" required minLength={8} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
            </div>
            <Button data-testid="set-initial-password-submit-button" type="submit" disabled={loading} className="w-full bg-petroleum hover:bg-petroleum/90 rounded-full h-11">
              {loading ? "Guardando..." : "Continuar a pago"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
